/* eslint-disable import/no-named-as-default */
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { useForm } from "react-hook-form";

import { formFieldVariants } from "./animations";
import { AgentAuthFormValues, agentAuthSchema } from "./schemas";
import { AUTH_TYPES } from "@/lib/auth/constants";
import FormOtpField from "@/components/common/forms/FormOtpField";
import FormTextField from "@/components/common/forms/FormTextField";
import Button from "@/components/ui/buttons/Button";
import { cn } from "@/lib/utils";

interface AgentAuthFormProps {
  onAuthenticated: () => void;
  isPage?: boolean;
}

type FormMessage = { message: string; type: "info" | "error" };

const OTP_LENGTH = 6;

const AgentAuthForm: React.FC<AgentAuthFormProps> = ({ onAuthenticated, isPage = false }) => {
  const t = useTranslations("Login.agent");

  const [method, setMethod] = useState<"otp" | "password">("otp");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [formMessage, setFormMessage] = useState<FormMessage | null>(null);
  const [loadingState, setLoadingState] = useState({ isRequestingCode: false, isSubmitting: false });

  const agentForm = useForm<AgentAuthFormValues>({
    resolver: zodResolver(agentAuthSchema),
    defaultValues: {
      method: "otp",
      email: "",
      code: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const handleRequestCode = async () => {
    const isEmailValid = await agentForm.trigger("email");

    if (!isEmailValid) {
      return;
    }

    setLoadingState((state) => ({ ...state, isRequestingCode: true }));
    setFormMessage(null);

    try {
      const response = await fetch("/api/auth/email-otp/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: agentForm.getValues("email") }),
      });

      if (response.status === 429) {
        setFormMessage({ message: t("rateLimited"), type: "error" });
        return;
      }

      if (!response.ok) {
        setFormMessage({ message: t("requestFailed"), type: "error" });
        return;
      }

      setIsCodeSent(true);
      agentForm.setValue("code", "");
      setFormMessage({ message: t("codeSent"), type: "info" });
    } catch (error) {
      console.error("Error requesting email OTP:", error);
      setFormMessage({ message: t("requestFailed"), type: "error" });
    } finally {
      setLoadingState((state) => ({ ...state, isRequestingCode: false }));
    }
  };

  const handleSignIn = async (authType: string, credentials: Record<string, string>, failureMessage: string) => {
    setLoadingState((state) => ({ ...state, isSubmitting: true }));
    setFormMessage(null);

    try {
      const response = await signIn(authType, { ...credentials, redirect: false });

      if (!response?.ok || response?.error) {
        setFormMessage({ message: failureMessage, type: "error" });
        return;
      }

      onAuthenticated();
    } catch (error) {
      console.error("Agent login error:", error);
      setFormMessage({ message: failureMessage, type: "error" });
    } finally {
      setLoadingState((state) => ({ ...state, isSubmitting: false }));
    }
  };

  const handleSubmit = async (data: AgentAuthFormValues) => {
    if (data.method === "password") {
      await handleSignIn(
        AUTH_TYPES.AGENT_PASSWORD,
        { email: data.email, password: data.password },
        t("invalidCredentials"),
      );
      return;
    }

    if (data.code?.length !== OTP_LENGTH) {
      agentForm.setError("code", { message: t("codeLengthError", { length: OTP_LENGTH }) });
      return;
    }

    await handleSignIn(AUTH_TYPES.EMAIL_OTP, { email: data.email, code: data.code }, t("invalidCode"));
  };

  const handleSwitchMethod = () => {
    const nextMethod = method === "otp" ? "password" : "otp";
    const email = agentForm.getValues("email");

    setMethod(nextMethod);
    setIsCodeSent(false);
    setFormMessage(null);
    agentForm.reset(
      nextMethod === "password" ? { method: "password", email, password: "" } : { method: "otp", email, code: "" },
    );
  };

  const isOtpStep = method === "otp" && isCodeSent;
  const primaryAction = method === "otp" && !isCodeSent ? handleRequestCode : agentForm.handleSubmit(handleSubmit);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void primaryAction();
  };

  return (
    <>
      {formMessage ? (
        <div className="flex items-center justify-center">
          <span
            className={cn(
              "inter text-center text-sm",
              formMessage.type === "error" ? "text-red-500" : "text-secondary-foreground",
            )}
          >
            {formMessage.message}
          </span>
        </div>
      ) : null}

      <form onSubmit={handleFormSubmit} className="space-y-4">
        <div className={cn("mx-auto w-full space-y-4", !isPage ? "md:max-w-[80%]" : "")}>
          <FormTextField
            control={agentForm.control}
            name="email"
            placeholder={t("emailPlaceholder")}
            className="rtl:placeholder:text-right"
            type="email"
            autoComplete="username"
            disabled={isOtpStep}
            required
          />

          <AnimatePresence mode="sync">
            {method === "password" && (
              <motion.div variants={formFieldVariants} initial="initial" animate="animate" exit="exit">
                <FormTextField
                  control={agentForm.control}
                  name="password"
                  placeholder={t("passwordPlaceholder")}
                  className="rtl:placeholder:text-right"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="sync">
            {isOtpStep && (
              <motion.div variants={formFieldVariants} initial="initial" animate="animate" exit="exit">
                <FormOtpField control={agentForm.control} name="code" length={OTP_LENGTH} label={t("codeLabel")} />
                <p className="inter mt-3 text-xs text-secondary-foreground">{t("codeExpiryHint")}</p>
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            buttonText={method === "password" ? t("login") : isCodeSent ? t("login") : t("sendCode")}
            className="mt-10 w-full rounded-lg bg-primary py-3 text-center text-white transition-all duration-300 hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            loadingState={loadingState.isRequestingCode || loadingState.isSubmitting}
            onClick={primaryAction}
          />

          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
            {isOtpStep ? (
              <button
                type="button"
                onClick={handleRequestCode}
                className="inter text-sm font-semibold text-primary underline underline-offset-4 transition-opacity duration-300 hover:opacity-80"
              >
                {t("resendCode")}
              </button>
            ) : (
              <span aria-hidden="true" />
            )}

            <button
              type="button"
              onClick={handleSwitchMethod}
              className="inter text-sm font-semibold text-secondary-foreground underline underline-offset-4 transition-colors duration-300 hover:text-primary"
            >
              {method === "otp" ? t("usePassword") : t("useCode")}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default AgentAuthForm;
